import { useState } from 'react';
import { Badge, Dropdown, Button, ListGroup, Spinner } from 'react-bootstrap';
import { useNotifications } from '../hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';

export const NotificationBell = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const {
    notifications,
    counts,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    isMarkingAllAsRead,
    isClearingAll
  } = useNotifications();

  const recentNotifications = notifications.slice(0, 5);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'TICKET_CREATED':
        return 'fas fa-ticket-alt text-primary';
      case 'TICKET_ASSIGNED':
        return 'fas fa-user-check text-success';
      case 'TICKET_STATUS_CHANGED':
        return 'fas fa-sync-alt text-info';
      case 'TICKET_PRIORITY_CHANGED':
        return 'fas fa-exclamation-triangle text-warning';
      case 'TICKET_COMMENT_ADDED':
        return 'fas fa-comment text-secondary';
      case 'SLA_FIRST_RESPONSE_DUE':
      case 'SLA_RESOLUTION_DUE':
        return 'fas fa-clock text-danger';
      case 'SLA_BREACHED':
        return 'fas fa-exclamation-circle text-danger';
      default:
        return 'fas fa-bell text-muted';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'TICKET_CREATED':
        return 'primary';
      case 'TICKET_ASSIGNED':
        return 'success';
      case 'TICKET_STATUS_CHANGED':
        return 'info';
      case 'TICKET_PRIORITY_CHANGED':
        return 'warning';
      case 'TICKET_COMMENT_ADDED':
        return 'secondary';
      case 'SLA_FIRST_RESPONSE_DUE':
      case 'SLA_RESOLUTION_DUE':
        return 'danger';
      case 'SLA_BREACHED':
        return 'danger';
      default:
        return 'muted';
    }
  };

  const handleNotificationClick = (notification: any) => {
    if (notification.status === 'UNREAD') {
      markAsRead(notification.id);
    }
    
    // Navigate to ticket if it exists
    if (notification.ticketId) {
      window.location.href = `/tickets/${notification.ticketId}`;
    }
    
    setShowDropdown(false);
  };

  return (
    <div className="notification-bell">
      <Dropdown show={showDropdown} onToggle={setShowDropdown} align="end">
      <Dropdown.Toggle
        as={Button}
        variant={counts.unread > 0 ? "outline-primary" : "outline-secondary"}
        className="position-relative"
        style={{ 
          minWidth: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <i className={`fas fa-bell fs-5 ${counts.unread > 0 ? 'text-primary' : 'text-muted'}`}></i>
        {counts.unread > 0 && (
          <Badge
            bg="danger"
            className="position-absolute top-0 start-100 translate-middle rounded-pill"
            style={{ fontSize: '0.7rem', minWidth: '18px', height: '18px' }}
          >
            {counts.unread > 99 ? '99+' : counts.unread}
          </Badge>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className="notification-dropdown" style={{ width: '350px', maxHeight: '500px' }}>
        <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
          <h6 className="mb-0">
            <i className="fas fa-bell me-2"></i>
            Notifications
            {counts.unread > 0 && (
              <Badge bg="primary" className="ms-2">
                {counts.unread}
              </Badge>
            )}
          </h6>
          <div className="d-flex gap-1">
            {counts.unread > 0 && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={markAllAsRead}
                disabled={isMarkingAllAsRead}
              >
                {isMarkingAllAsRead ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="fas fa-check"></i>
                )}
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={clearAll}
                disabled={isClearingAll}
              >
                {isClearingAll ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="fas fa-trash"></i>
                )}
              </Button>
            )}
          </div>
        </div>

        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {isLoading ? (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" />
              <div className="mt-2 text-muted">Loading notifications...</div>
            </div>
          ) : recentNotifications.length === 0 ? (
            <div className="text-center p-4 text-muted">
              <i className="fas fa-bell-slash fs-1 mb-3"></i>
              <div>No notifications yet</div>
            </div>
          ) : (
            <ListGroup variant="flush">
              {recentNotifications.map((notification: any) => (
                <ListGroup.Item
                  key={notification.id}
                  className={`notification-item ${
                    notification.status === 'UNREAD' ? 'bg-light' : ''
                  }`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="d-flex align-items-start">
                    <div className="me-3">
                      <i className={`${getNotificationIcon(notification.type)} fs-5`}></i>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start">
                        <h6 className={`mb-1 text-${getNotificationColor(notification.type)}`}>
                          {notification.title}
                        </h6>
                        {notification.status === 'UNREAD' && (
                          <div className="bg-primary rounded-circle" style={{ width: '8px', height: '8px' }}></div>
                        )}
                      </div>
                      <p className="mb-1 text-muted small">{notification.message}</p>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                        </small>
                        {notification.ticket && (
                          <Badge bg="outline-secondary" className="small">
                            {notification.ticket.priority}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="ms-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>

        {notifications.length > 5 && (
          <div className="text-center p-2 border-top">
            <Button variant="link" size="sm" className="text-decoration-none">
              View all notifications
            </Button>
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
    </div>
  );
};
